/**
 * Locale configuration shared by every app in the monorepo.
 *
 * Two tiers of locale exist, and the distinction matters:
 *
 *   • **Built-in** (`fa`, `en`) — their catalogs live in git under
 *     `packages/i18n/messages/<locale>/`. They define the canonical key set
 *     that developers write against, and they ship with the bundle.
 *
 *   • **Runtime** (anything an admin adds later) — no file, no deploy. The
 *     `languages` table declares them and the `ui_messages` overlay supplies
 *     their values. Any key they are missing falls back to Persian.
 *
 * A developer must never create e.g. `messages/ar/common.json`. Adding a
 * language is a product operation, not a code change.
 */

/** Source-of-truth locale. Every other locale falls back to this one. */
export const DEFAULT_LOCALE = 'fa';

/** Locales whose catalogs are tracked in git. */
export const BUILTIN_LOCALES = ['fa', 'en'] as const;

export type BuiltinLocale = (typeof BUILTIN_LOCALES)[number];

/**
 * Any BCP-47-ish language code. Deliberately `string` rather than a union:
 * the set of valid locales is decided at runtime by the database, so the
 * type system cannot (and must not) enumerate it.
 */
export type Locale = string;

/** Cookie next-intl uses to remember the visitor's choice. */
export const LOCALE_COOKIE = 'NEXT_LOCALE';

/**
 * Header the NestJS `LocalizationMiddleware` already reads
 * (`apps/backend/src/i18n/localization.middleware.ts`).
 */
export const LOCALE_HEADER = 'x-language';

/** Catalog namespaces. Adding one here requires a file in every built-in locale. */
export const NAMESPACES = [
  'common',
  'homepage',
  'auth',
  'orders',
  'store',
  'blog',
  'panel',
  'errors',
  // The admin panel's own UI. Added when the panel entered scope; it is a
  // separate surface from `panel`, which is the customer-facing dashboard.
  'admin',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export type Direction = 'rtl' | 'ltr';

/**
 * Languages conventionally written right-to-left.
 *
 * This is only a *default* used to pre-fill the admin form and to render
 * sensibly before the database has been consulted. Once a `Language` row
 * exists, its `direction` column wins — an admin may legitimately disagree
 * with this list.
 */
const RTL_LOCALES = new Set([
  'fa', // Persian
  'ar', // Arabic
  'he', // Hebrew
  'ur', // Urdu
  'ps', // Pashto
  'sd', // Sindhi
  'ckb', // Central Kurdish
  'ku', // Kurdish
  'yi', // Yiddish
  'dv', // Dhivehi
]);

/** Lower-cases and strips any region/script subtag: `en-US` → `en`. */
export function normalizeLocale(code: string | null | undefined): Locale {
  if (!code) return DEFAULT_LOCALE;
  const base = code.trim().toLowerCase().split(/[-_]/)[0];
  return base || DEFAULT_LOCALE;
}

export function isBuiltinLocale(code: string | null | undefined): code is BuiltinLocale {
  return (BUILTIN_LOCALES as readonly string[]).includes(normalizeLocale(code));
}

/**
 * Best-guess writing direction for a locale.
 *
 * Prefer the `direction` stored on the `Language` row when you have it; this
 * is the fallback for when you don't (middleware cold start, unknown code).
 */
export function getDefaultDirection(code: string | null | undefined): Direction {
  return RTL_LOCALES.has(normalizeLocale(code)) ? 'rtl' : 'ltr';
}

/**
 * Picks the best supported locale for a visitor.
 *
 * `supported` is expected to come from the database at runtime, so this
 * cannot be a compile-time lookup.
 */
export function resolveLocale(
  requested: string | null | undefined,
  supported: readonly string[] = BUILTIN_LOCALES,
): Locale {
  const normalized = normalizeLocale(requested);
  const pool = supported.map(normalizeLocale);
  if (pool.includes(normalized)) return normalized;
  return pool.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : (pool[0] ?? DEFAULT_LOCALE);
}

/**
 * Parses an `Accept-Language` header into normalized codes, best match first.
 * Falls back to insertion order when q-values are absent or equal.
 */
export function parseAcceptLanguage(header: string | null | undefined): Locale[] {
  if (!header) return [];
  return header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params
        .map((p) => p.trim())
        .find((p) => p.startsWith('q='));
      const quality = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag: normalizeLocale(tag), quality: Number.isNaN(quality) ? 0 : quality };
    })
    .filter((entry) => entry.tag)
    .sort((a, b) => b.quality - a.quality)
    .map((entry) => entry.tag);
}
